import { Category, Expense } from '@easy-expense/data-schema-v2';
import Data from '@easy-expense/frontend-data-layer';
import { getTranslation, useIntlStore } from '@easy-expense/intl-client';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer, zIndex } from '@easy-expense/ui-web-core';
import cuid from 'cuid';
import { useFormikContext } from 'formik';
import React from 'react';
import { z } from 'zod';

import { CurrencyField } from './CurrencyInput.component';
import { SearchDropdown } from '../../Shared/Dropdown/SearchDropDown.component';

export const SplitExpenseFormSchema = z.object({
  splits: z.array(
    z.object({
      total: z.number().optional(),
      category: z.string().optional(),
      key: z.string(),
    }),
  ),
  expense: z.custom<Partial<Expense>>(),
});

export type SplitExpenseFormValues = z.infer<typeof SplitExpenseFormSchema>;

const roundCurrency = (num: number) => {
  return Math.round(num * 100) / 100;
};

export const SplitExpenseForm: React.FC<{
  vendor: string;
  total: number;
  expense: Partial<Expense>;
}> = ({ vendor, total, expense }) => {
  const { values, setFieldValue } = useFormikContext<SplitExpenseFormValues>();
  const { formatCurrency } = useIntlStore();
  const categories: Category[] = Data.expenseCategories.use();

  const [focusedIndex, setFocusedIndex] = React.useState<number | undefined>();
  const autoBalanceTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastProcessedValuesRef = React.useRef<string>('');
  const latestValuesRef = React.useRef(values.splits);
  const latestFocusedIndexRef = React.useRef(focusedIndex);

  // Keep refs in sync with current values
  React.useEffect(() => {
    latestValuesRef.current = values.splits;
  }, [values.splits]);

  React.useEffect(() => {
    latestFocusedIndexRef.current = focusedIndex;
  }, [focusedIndex]);

  // Calculate total error from current form values
  const totalError = React.useMemo(() => {
    const sumOfSplits = values.splits.reduce((sum, split) => {
      const val = split.total;
      // Handle NaN, undefined, null, and 0 values (treat 0 as undefined)
      if (val === undefined || val === null || isNaN(val) || val === 0) {
        return sum;
      }
      return sum + val;
    }, 0);
    return roundCurrency(sumOfSplits - total);
  }, [values.splits, total]);

  // Auto-balance: adjust non-focused splits to balance the total
  // Debounce rapid updates to handle fast typing
  React.useEffect(() => {
    // Skip if no focused field or error is negligible
    if (focusedIndex === undefined || Math.abs(totalError) < 0.01) {
      return;
    }

    // Create a signature of the current values to detect actual changes
    const currentSignature = JSON.stringify(
      values.splits.map((split, index) => ({ index, total: split.total })),
    );

    // Skip if values haven't actually changed (prevents infinite loops)
    if (currentSignature === lastProcessedValuesRef.current) {
      return;
    }

    // Clear any pending auto-balance
    if (autoBalanceTimeoutRef.current) {
      clearTimeout(autoBalanceTimeoutRef.current);
    }

    // Debounce rapid updates - this batches multiple rapid changes
    autoBalanceTimeoutRef.current = setTimeout(() => {
      // Use refs to get the latest values (they're updated on every render)
      const currentSplits = latestValuesRef.current;
      const currentFocusedIndex = latestFocusedIndexRef.current;

      if (currentFocusedIndex === undefined) {
        return;
      }

      const focusedValue =
        currentFocusedIndex !== undefined ? currentSplits[currentFocusedIndex]?.total : undefined;

      // Skip if focused value is invalid
      if (
        focusedValue === undefined ||
        focusedValue === null ||
        isNaN(focusedValue) ||
        focusedValue <= 0
      ) {
        lastProcessedValuesRef.current = JSON.stringify(
          currentSplits.map((split, index) => ({ index, total: split.total })),
        );
        return;
      }

      // Calculate current error
      const currentSum = currentSplits.reduce((sum, split) => {
        const val = split.total;
        if (val === undefined || val === null || isNaN(val) || val === 0) {
          return sum;
        }
        return sum + val;
      }, 0);
      const currentError = roundCurrency(currentSum - total);

      // Only auto-balance if there's a significant error
      if (Math.abs(currentError) >= 0.01) {
        // Distribute the error across non-focused splits sequentially
        let remainingError = currentError;
        currentSplits.forEach((split, index) => {
          if (index === currentFocusedIndex || remainingError === 0) {
            return;
          }

          const currentTotal = split.total;
          // Skip if undefined, null, NaN, or zero
          if (
            currentTotal === undefined ||
            currentTotal === null ||
            isNaN(currentTotal) ||
            currentTotal <= 0
          ) {
            return;
          }

          // Calculate correction for this split
          const correction = roundCurrency(Math.max(currentTotal - remainingError, 0));

          // Only update if the split has a value and we need to adjust
          if (currentTotal > 0 || remainingError < 0) {
            // Convert 0 to undefined
            const safeCorrection = correction === 0 ? undefined : correction;
            setFieldValue('splits[' + index + '].total', safeCorrection);
            // Update remaining error after this correction
            remainingError = roundCurrency(remainingError - (currentTotal - (correction || 0)));
          }
        });
      }

      // Mark these values as processed
      lastProcessedValuesRef.current = JSON.stringify(
        currentSplits.map((split, index) => ({ index, total: split.total })),
      );
    }, 50); // Short delay to batch rapid updates

    return () => {
      if (autoBalanceTimeoutRef.current) {
        clearTimeout(autoBalanceTimeoutRef.current);
      }
    };
  }, [values.splits, focusedIndex, totalError, total, setFieldValue]);

  return (
    <Layout.Column px={32} py={32} style={{ maxWidth: 600, width: '100%' }}>
      <Layout.Column>
        <Layout.Column align>
          <OpenSans.Secondary size={12} weight="bold-700">
            {getTranslation('SPLIT TRANSACTION')}
          </OpenSans.Secondary>
          <Spacer.Vertical size={16} />
          <OpenSans.Primary size={28} weight="bold-700">
            {vendor}
          </OpenSans.Primary>
          <Spacer.Vertical size={8} />
          {Math.abs(totalError) < 0.01 ? (
            <OpenSans.Primary size={20} weight="regular-400">
              {formatCurrency(total)}
            </OpenSans.Primary>
          ) : (
            <OpenSans.Primary
              size={20}
              weight="regular-400"
              style={{ color: theme.colors.destructive }}
            >
              {getTranslation('Total off by')} {formatCurrency(Math.abs(totalError))}
            </OpenSans.Primary>
          )}
        </Layout.Column>

        <Spacer.Vertical size={32} />
        <Layout.Column>
          {values.splits.map((split, index) => (
            <Layout.Column key={split.key}>
              {index > 0 && (
                <>
                  <Layout.Column
                    style={{
                      height: 1,
                      backgroundColor: theme.colors.grayLight,
                      width: '100%',
                      marginBottom: 16,
                    }}
                  />
                </>
              )}
              <Layout.Row align style={{ marginBottom: 16 }}>
                <Layout.Column
                  style={{
                    flex: 2,
                    marginRight: 16,
                    position: 'relative',
                  }}
                >
                  <SearchDropdown
                    header=""
                    placeholder={getTranslation('Category Name')}
                    selectedKey={split.category}
                    autoCompleteValues={categories}
                    onSelect={(category) => {
                      if (category?.key) {
                        setFieldValue('splits[' + index + '].category', category.key);
                      }
                    }}
                    error={!split.category}
                    iconicIcon="pricetags-outline"
                    clearOnSelect={true}
                    customZIndex={zIndex.Dropdown + index}
                  />
                </Layout.Column>

                <Layout.Column style={{ flex: 1 }}>
                  <CurrencyField
                    initialValue={split.total}
                    updatedValue={
                      split.total !== undefined && !isNaN(split.total) && split.total !== 0
                        ? split.total
                        : undefined
                    }
                    onChange={(value) => {
                      setFocusedIndex(index);
                      // Handle NaN and 0 - set to undefined instead
                      const safeValue = !isNaN(value) && value !== 0 ? value : undefined;
                      setFieldValue('splits[' + index + '].total', safeValue);
                    }}
                    label=""
                    showCurrency={false}
                    allowNegative={false}
                  />
                </Layout.Column>
              </Layout.Row>
            </Layout.Column>
          ))}
        </Layout.Column>

        <Spacer.Vertical size={16} />

        <Layout.Row justify>
          <Layout.PressableRow
            onClick={() => {
              setFieldValue('splits', [
                ...values.splits,
                { category: expense.category, total: undefined, key: cuid() },
              ]);
            }}
            style={{ background: 'transparent' }}
          >
            <OpenSans.Primary weight="bold-700" style={{ color: theme.colors.brandPrimary }}>
              {getTranslation('Add Split')} (+)
            </OpenSans.Primary>
          </Layout.PressableRow>
        </Layout.Row>
      </Layout.Column>
    </Layout.Column>
  );
};
